import React, { useState, useRef } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { Modal } from '../../components/ui';
import { errMsg } from '../../utils/format';
import api from '../../utils/api';
import { useNavigate } from 'react-router-dom';


function ImageGallery({ productId }) {
  const { data: images, refetch } = useFetch(`/api/products/${productId}/images`);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef();

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('image', file);
      await api.post(`/api/products/${productId}/images`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      refetch();
    } catch (err) { alert(errMsg(err)); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const setPrimary = async (imgId) => {
    try { await api.patch(`/api/products/${productId}/images/${imgId}`, {}); refetch(); }
    catch (err) { alert(errMsg(err)); }
  };

  const deleteImage = async (imgId) => {
    if (!window.confirm('Delete this image?')) return;
    try { await api.delete(`/api/products/${productId}/images/${imgId}`); refetch(); }
    catch (err) { alert(errMsg(err)); }
  };

  const imgs = images || [];
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
        <label style={{ fontSize:12, fontWeight:500, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:'0.04em' }}>
          Images ({imgs.length}/10)
        </label>
        {imgs.length < 10 && (
          <button type="button" className="btn btn-sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
            <i className="ti ti-upload" /> {uploading ? 'Uploading…' : 'Upload'}
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }} onChange={upload} />
      </div>
      {imgs.length === 0 ? (
        <div style={{ padding:'16px', textAlign:'center', color:'var(--text-tertiary)', fontSize:12,
          border:'1px dashed var(--border)', borderRadius:8 }}>
          No images — click Upload to add
        </div>
      ) : (
        <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
          {imgs.map(img => (
            <div key={img.id} style={{ position:'relative', width:80, height:80 }}>
              <img src={img.url} alt={img.alt_text || ''} onClick={() => setLightbox(img.url)}
                style={{ width:80, height:80, objectFit:'cover', borderRadius:6, cursor:'pointer',
                  border: img.is_primary ? '2px solid var(--blue)' : '1px solid var(--border)' }} />
              <button type="button" onClick={() => setPrimary(img.id)}
                style={{ position:'absolute', top:2, left:2, background:'rgba(0,0,0,0.5)',
                  border:'none', borderRadius:'50%', width:18, height:18, cursor:'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}>
                <i className={`ti ${img.is_primary ? 'ti-star-filled' : 'ti-star'}`}
                  style={{ fontSize:10, color: img.is_primary ? '#fbbf24' : '#fff' }} />
              </button>
              <button type="button" onClick={() => deleteImage(img.id)}
                style={{ position:'absolute', top:2, right:2, background:'rgba(0,0,0,0.5)',
                  border:'none', borderRadius:'50%', width:18, height:18, cursor:'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}>
                <i className="ti ti-x" style={{ fontSize:10, color:'#fff' }} />
              </button>
            </div>
          ))}
        </div>
      )}
      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:9999,
            display:'flex', alignItems:'center', justifyContent:'center', cursor:'zoom-out' }}>
          <img src={lightbox} alt="" style={{ maxWidth:'90vw', maxHeight:'90vh', borderRadius:8 }} />
        </div>
      )}
    </div>
  );
}

function TinyEditor({ value, onChange }) {
  const containerRef = React.useRef(null);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const currentContentRef = React.useRef(value || '');
  const [mode, setMode] = React.useState('visual');
  const [htmlValue, setHtmlValue] = React.useState(value || '');
  const initDone = React.useRef(false);

  // Sync content ref when value changes from outside
  React.useEffect(() => {
    currentContentRef.current = value || '';
    if (!initDone.current) setHtmlValue(value || '');
  }, [value]); // eslint-disable-line

  React.useEffect(() => {
    if (mode !== 'visual') return;
    initDone.current = false;

    const load = (cb) => {
      if (window.tinymce) { cb(); return; }
      if (!document.getElementById('tinymce-script')) {
        const s = document.createElement('script');
        s.id = 'tinymce-script';
        s.src = 'https://cdn.jsdelivr.net/npm/tinymce@6/tinymce.min.js';
        s.onload = cb;
        document.head.appendChild(s);
      } else {
        const iv = setInterval(() => { if (window.tinymce) { clearInterval(iv); cb(); } }, 100);
      }
    };

    let editorInstance = null;

    const init = () => {
      const el = containerRef.current;
      if (!el || !window.tinymce) return;

      // Create a fresh div for TinyMCE to attach to
      el.innerHTML = '';
      const div = document.createElement('div');
      el.appendChild(div);

      window.tinymce.init({
        target: div,
        inline: false,
        height: 600,
        menubar: false,
        plugins: 'table lists link code autolink',
        toolbar: 'undo redo | blocks | bold italic underline | bullist numlist | table | link | code | removeformat',
        content_style: 'body { font-family: Arial,sans-serif; font-size: 14px; }',
        branding: false,
        promotion: false,
        license_key: 'gpl',
        setup: (editor) => {
          editorInstance = editor;
          editor.on('init', () => {
            editor.setContent(currentContentRef.current || '');
            initDone.current = true;
          });
          editor.on('change input keyup', () => {
            const html = editor.getContent();
            currentContentRef.current = html;
            onChangeRef.current(html);
          });
        },
      });
    };

    load(init);

    return () => {
      if (editorInstance) {
        try { editorInstance.destroy(); } catch {}
        editorInstance = null;
      }
      initDone.current = false;
    };
  }, [mode]); // eslint-disable-line

  if (mode === 'html') {
    return (
      <div>
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:4 }}>
          <button type="button" className="btn btn-sm" onClick={() => {
            setMode('visual');
          }}>
            Switch to Visual
          </button>
        </div>
        <textarea value={htmlValue}
          onChange={e => { setHtmlValue(e.target.value); currentContentRef.current = e.target.value; onChangeRef.current(e.target.value); }}
          style={{ width:'100%', minHeight:600, fontFamily:'monospace', fontSize:12,
            resize:'vertical', border:'1px solid var(--border)', borderRadius:6, padding:8 }} />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:4 }}>
        <button type="button" className="btn btn-sm" onClick={() => {
          setHtmlValue(currentContentRef.current);
          setMode('html');
        }}>
          <i className="ti ti-code" /> HTML
        </button>
      </div>
      <div ref={containerRef} />
    </div>
  );
}

export default function EditProductModal({ product, onClose, onSaved, onOpenKit, onOpenAlert, onOpenLog, inline }) {
  const navigate = useNavigate();
  const { data: shopifyAccounts } = useFetch('/api/shopify-accounts');
  const { data: existingAssignments } = useFetch(`/api/products/${product.id}/store-assignments`);
  const [form, setForm] = useState({
    sku: product.sku || '',
    name: product.name || '',
    unitCost: product.unit_cost || 0,
    unitPrice: product.unit_price || 0,
    compareAtPrice: product.compare_at_price || '',
    weight: product.weight || '',
    quantityOnHand: product.quantity_on_hand || 0,
    quantityAtAmazon: product.quantity_at_amazon || 0,
    amazonAsin: product.amazon_asin || '',
    imageUrl: product.image_url || '',
    notes: product.notes || '',
    includeInVendorFeed: product.include_in_vendor_feed || false,
    alertsEnabled: product.alerts_enabled !== false,
    lengthIn: product.length_in ?? '',
    widthIn: product.width_in ?? '',
    heightIn: product.height_in ?? '',
    description: product.description || '',
    hazmatEnabled:     product.hazmat_enabled     || false,
    hazmatUnNumber:    product.hazmat_un_number    || '',
    hazmatProperName:  product.hazmat_proper_name  || '',
    hazmatClass:       product.hazmat_class        || '9',
    hazmatPackingGroup:product.hazmat_packing_group|| '',
    hazmatWhPerUnit:   product.hazmat_wh_per_unit  ?? '',
    hazmatCellsPerUnit:product.hazmat_cells_per_unit ?? '',
    hazmatMode:        product.hazmat_mode         || 'ground',
    hazmatPackaging:   product.hazmat_packaging    || 'FiberboardBox',
    signatureRequired: product.signature_required  || false,
    lengthIn: product.length_in ?? '',
    widthIn: product.width_in ?? '',
    heightIn: product.height_in ?? '',
    vendorItemId: product.vendor_item_id || '',
  });
  const [storeAssignments, setStoreAssignments] = React.useState({});
  const [saving, setSaving] = useState(false);
  const [pushingSku, setPushingSku] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  React.useEffect(() => {
    if (existingAssignments) {
      const map = {};
      existingAssignments.forEach(a => {
        map[a.shopify_account_id] = {
          assigned: true,
          shopifyProductId: a.shopify_product_id || '',
          shopifyVariantId: a.shopify_variant_id || '',
        };
      });
      setStoreAssignments(map);
    }
  }, [existingAssignments]);

  const pushSku = async () => {
    setPushingSku(true);
    try {
      const { data: res } = await api.post(`/api/products/${product.id}/push-sku`);
      alert(res.message + (res.failed?.length ? `\n\nFailed: ${res.failed.map(f => f.storeName + ' — ' + f.error).join('; ')}` : ''));
    } catch (err) { alert(errMsg(err)); }
    finally { setPushingSku(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/api/products/${product.id}`, form);
      const assignments = Object.entries(storeAssignments)
        .filter(([_, v]) => v.assigned)
        .map(([accountId, v]) => ({
          shopifyAccountId: parseInt(accountId),
          shopifyProductId: v.shopifyProductId || null,
          shopifyVariantId: v.shopifyVariantId || null,
        }));
      await api.put(`/api/products/${product.id}/store-assignments`, { assignments });
      onSaved();
      onClose();
    } catch (err) { alert(errMsg(err)); }
    finally { setSaving(false); }
  };

  const [syncing, setSyncing] = useState(false);
  const syncToShopify = async () => {
    if (!window.confirm('Push name, description, price, weight and image to Shopify?')) return;
    setSyncing(true);
    try { await api.post(`/api/products/${product.id}/push-to-shopify`); alert('Pushed to Shopify successfully.'); }
    catch (err) { alert(errMsg(err)); }
    finally { setSyncing(false); }
  };

  const footer = (
    <div style={{ display:'flex', justifyContent:'space-between', width:'100%' }}>
      <button className="btn btn-danger btn-sm" onClick={async () => {
        if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
        try { const { data: r } = await api.delete(`/api/products/${product.id}`); onSaved(r.message); onClose(); }
        catch (err) { alert(errMsg(err)); }
      }}><i className="ti ti-trash" /> Delete</button>
      <div style={{ display:'flex', gap:8 }}>
        <button className="btn btn-sm" onClick={syncToShopify} disabled={syncing}>
          <i className="ti ti-brand-shopify" /> {syncing ? 'Syncing…' : 'Sync to Shopify'}
        </button>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" form="edit-product-form" type="submit">{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  );

  if (inline) return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100%' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 16px', borderBottom:'0.5px solid var(--card-border)', background:'var(--card-bg)', position:'sticky', top:0, zIndex:10 }}>
        <span style={{ fontWeight:600, fontSize:15 }}>Edit — {product.name} ({product.sku})</span>
        <button className="btn btn-icon" onClick={onClose}><i className="ti ti-x" /></button>
      </div>
      <div style={{ padding:'16px' }}>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
          <button type="button" className="btn btn-sm" onClick={() => { onClose(); onOpenKit && onOpenKit(product); }}>
            <i className="ti ti-packages" /> {product.kit_id ? 'Edit bundle' : 'Convert to bundle'}
          </button>
          <button type="button" className="btn btn-sm" onClick={() => { onClose(); onOpenAlert && onOpenAlert(product); }}>
            <i className="ti ti-bell" /> Alert thresholds
          </button>
          <button type="button" className="btn btn-sm" onClick={() => { onClose(); onOpenLog && onOpenLog(product); }}>
            <i className="ti ti-history" /> Inventory log
          </button>
        </div>
        <form id="edit-product-form" onSubmit={save}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>

          {/* LEFT COLUMN */}
          <div>
            {/* Image */}
            {form.imageUrl && (
              <div style={{ marginBottom:16, textAlign:'center' }}>
                <img src={form.imageUrl} alt={form.name}
                  style={{ maxWidth:'100%', maxHeight:200, objectFit:'contain', borderRadius:6, border:'1px solid var(--border)' }}
                  onError={e => e.target.style.display='none'}
                />
              </div>
            )}


            <div className="form-row">
              <div className="form-group">
                <label>SKU *</label>
                <input required value={form.sku} onChange={e => set('sku', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Vendor Item ID</label>
                <input value={form.vendorItemId} onChange={e => set('vendorItemId', e.target.value)} placeholder="Wintron itemid" />
              </div>
            </div>

            <div className="form-group">
              <label>Product name *</label>
              <input required value={form.name} onChange={e => set('name', e.target.value)} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Unit cost ($)</label>
                <input type="number" step="0.0001" min="0" value={form.unitCost} onChange={e => set('unitCost', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Price / MAP ($)</label>
                <input type="number" step="0.0001" min="0" value={form.unitPrice} onChange={e => set('unitPrice', e.target.value)} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Compare-at price ($)</label>
                <input type="number" step="0.0001" min="0" value={form.compareAtPrice} onChange={e => set('compareAtPrice', e.target.value)} placeholder="List price" />
              </div>
              <div className="form-group">
                <label>Weight (lbs)</label>
                <input type="number" step="0.0001" min="0" value={form.weight} onChange={e => set('weight', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Dimensions (in) L × W × H</label>
                <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                  <input type="number" step="0.01" min="0" value={form.lengthIn} onChange={e => set('lengthIn', e.target.value)} placeholder="L" style={{ flex:1 }} />
                  <span style={{ color:'var(--text-tertiary)', fontSize:12 }}>×</span>
                  <input type="number" step="0.01" min="0" value={form.widthIn} onChange={e => set('widthIn', e.target.value)} placeholder="W" style={{ flex:1 }} />
                  <span style={{ color:'var(--text-tertiary)', fontSize:12 }}>×</span>
                  <input type="number" step="0.01" min="0" value={form.heightIn} onChange={e => set('heightIn', e.target.value)} placeholder="H" style={{ flex:1 }} />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Warehouse qty</label>
                <input type="number" min="0" value={form.quantityOnHand} onChange={e => set('quantityOnHand', e.target.value)} />
              </div>
              <div className="form-group">

              </div>
            </div>

            <div className="form-group">

            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} />
            </div>

            <div style={{ display:'flex', gap:20, marginTop:4 }}>
              <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:13, fontWeight:'normal' }}>
                <input type="checkbox" style={{ width:'auto' }}
                  checked={form.includeInVendorFeed || false}
                  onChange={e => set('includeInVendorFeed', e.target.checked)} />
                Include in vendor feed
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:13, fontWeight:'normal' }}>
                <input type="checkbox" style={{ width:'auto' }}
                  checked={form.alertsEnabled !== false}
                  onChange={e => set('alertsEnabled', e.target.checked)} />
                Alerts enabled
              </label>
            </div>
            <div className="form-group" style={{ marginTop:12 }}>
              <label>Dimensions (in) L × W × H</label>
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <input type="number" step="0.01" min="0" value={form.lengthIn} onChange={e => set('lengthIn', e.target.value)} placeholder="L" style={{ flex:1 }} />
                <span style={{ color:'var(--text-tertiary)', fontSize:12 }}>×</span>
                <input type="number" step="0.01" min="0" value={form.widthIn} onChange={e => set('widthIn', e.target.value)} placeholder="W" style={{ flex:1 }} />
                <span style={{ color:'var(--text-tertiary)', fontSize:12 }}>×</span>
                <input type="number" step="0.01" min="0" value={form.heightIn} onChange={e => set('heightIn', e.target.value)} placeholder="H" style={{ flex:1 }} />
              </div>
            </div>
            <div style={{ marginTop:12 }}>
              <ImageGallery productId={product.id} />
            </div>
            {shopifyAccounts && shopifyAccounts.length > 0 && (
              <details style={{ borderTop:'0.5px solid var(--card-border)', paddingTop:12, marginTop:8 }}>
                <summary style={{ fontWeight:500, fontSize:13, cursor:'pointer', marginBottom:10, userSelect:'none' }}>
                  Shopify Store Assignments
                </summary>
                {shopifyAccounts.map(acct => {
                  const assignment = storeAssignments[acct.id] || { assigned:false, shopifyProductId:'', shopifyVariantId:'' };
                  return (
                    <div key={acct.id} style={{ background:'var(--page-bg)', borderRadius:8, padding:10, marginBottom:8 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:assignment.assigned?10:0 }}>
                        <input type="checkbox" id={`store-${acct.id}`} style={{ width:'auto' }}
                          checked={assignment.assigned}
                          onChange={e => setStoreAssignments(s => ({ ...s, [acct.id]:{ ...assignment, assigned:e.target.checked } }))} />
                        <label htmlFor={`store-${acct.id}`} style={{ marginBottom:0, fontWeight:500, cursor:'pointer' }}>
                          {acct.name} <span style={{ fontSize:11, color:'var(--text-tertiary)', fontWeight:400 }}>({acct.store_url})</span>
                        </label>
                      </div>
                      {assignment.assigned && (
                        <div className="form-row" style={{ marginLeft:24 }}>
                          <div className="form-group" style={{ margin:0 }}>
                            <label style={{ fontSize:11 }}>Shopify Variant ID</label>
                            <input value={assignment.shopifyVariantId}
                              onChange={e => setStoreAssignments(s => ({ ...s, [acct.id]:{ ...assignment, shopifyVariantId:e.target.value } }))}
                              placeholder="e.g. 123456789" />
                          </div>
                          <div className="form-group" style={{ margin:0 }}>
                            <label style={{ fontSize:11 }}>Shopify Product ID</label>
                            <input value={assignment.shopifyProductId}
                              onChange={e => setStoreAssignments(s => ({ ...s, [acct.id]:{ ...assignment, shopifyProductId:e.target.value } }))}
                              placeholder="e.g. 987654321" />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </details>
            )}
            {/* HazMat Section */}
            <details style={{ borderTop:'0.5px solid var(--card-border)', paddingTop:12, marginTop:8 }}>
              <summary style={{ fontWeight:500, fontSize:13, cursor:'pointer', marginBottom:10, userSelect:'none' }}>
                <i className="ti ti-alert-triangle" style={{ marginRight:6, color:'var(--amber)' }} />
                HazMat Shipping
              </summary>
              <div style={{ background:'var(--page-bg)', borderRadius:8, padding:12 }}>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', marginBottom:12 }}>
                  <input type="checkbox" style={{ width:'auto' }}
                    checked={form.hazmatEnabled || false}
                    onChange={e => set('hazmatEnabled', e.target.checked)} />
                  <span style={{ fontWeight:500, fontSize:13 }}>This product ships as HazMat</span>
                </label>
                {form.hazmatEnabled && (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                    <div className="form-group">
                      <label>UN Number</label>
                      <select value={form.hazmatUnNumber} onChange={e => set('hazmatUnNumber', e.target.value)}>
                        <option value="">Select…</option>
                        <option value="UN3480">UN3480 — Lithium ion batteries</option>
                        <option value="UN3481">UN3481 — Lithium ion batteries in equipment</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Packing Group</label>
                      <select value={form.hazmatPackingGroup} onChange={e => set('hazmatPackingGroup', e.target.value)}>
                        <option value="">Select…</option>
                        <option value="II">II</option>
                        <option value="III">III</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ gridColumn:'1/-1' }}>
                      <label>Proper Shipping Name</label>
                      <input value={form.hazmatProperName}
                        onChange={e => set('hazmatProperName', e.target.value)}
                        placeholder="e.g. LITHIUM ION BATTERY" />
                    </div>
                    <div className="form-group">
                      <label>Hazard Class</label>
                      <input value={form.hazmatClass} onChange={e => set('hazmatClass', e.target.value)}
                        placeholder="9" />
                    </div>
                    <div className="form-group">
                      <label>Ship Mode</label>
                      <select value={form.hazmatMode} onChange={e => set('hazmatMode', e.target.value)}>
                        <option value="ground">Ground only</option>
                        <option value="air">Air only</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Watt-hours per battery (Wh)</label>
                      <input type="number" step="0.1" min="0" value={form.hazmatWhPerUnit}
                        onChange={e => set('hazmatWhPerUnit', e.target.value)} placeholder="e.g. 256" />
                    </div>
                    <div className="form-group">
                      <label>Cells per battery</label>
                      <input type="number" min="1" value={form.hazmatCellsPerUnit}
                        onChange={e => set('hazmatCellsPerUnit', e.target.value)} placeholder="e.g. 4" />
                    </div>
                    <div className="form-group" style={{ gridColumn:'1/-1' }}>
                      <label>Packaging Type</label>
                      <input value={form.hazmatPackaging} onChange={e => set('hazmatPackaging', e.target.value)}
                        placeholder="FiberboardBox" />
                    </div>
                  </div>
                )}
              </div>
            </details>
          </div>


            {/* Signature Required Section */}
            <details style={{ borderTop:'0.5px solid var(--card-border)', paddingTop:12, marginTop:8 }}>
              <summary style={{ fontWeight:500, fontSize:13, cursor:'pointer', marginBottom:10, userSelect:'none' }}>
                <i className="ti ti-writing-sign" style={{ marginRight:6, color:'var(--accent)' }} />
                Delivery Options
              </summary>
              <div style={{ background:'var(--page-bg)', borderRadius:8, padding:12 }}>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                  <input type="checkbox" style={{ width:'auto' }}
                    checked={form.signatureRequired || false}
                    onChange={e => set('signatureRequired', e.target.checked)} />
                  <span style={{ fontWeight:500, fontSize:13 }}>Require signature on delivery</span>
                </label>
                <p style={{ fontSize:11, color:'var(--text-secondary)', margin:'6px 0 0 24px' }}>
                  When enabled, UPS will require an adult signature upon delivery for orders containing this product.
                </p>
              </div>
            </details>
          {/* RIGHT COLUMN — Description */}
          <div style={{ display:'flex', flexDirection:'column' }}>
            <div className="form-group" style={{ flex:1, display:'flex', flexDirection:'column' }}>
              <label>Description</label>
              <TinyEditor
                value={form.description}
                onChange={val => set('description', val)}
              />
            </div>

          </div>
        </div>
      </form>
      </div>
      <div style={{ padding:'12px 16px', borderTop:'0.5px solid var(--card-border)' }}>
        {footer}
      </div>
    </div>
  );

  return (
    <Modal title={`Edit — ${product.sku}`} onClose={onClose} wide extraWide footer={footer}>
      <form id="edit-product-form" onSubmit={save}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>

          {/* LEFT COLUMN */}
          <div>
            {/* Image */}
            {form.imageUrl && (
              <div style={{ marginBottom:16, textAlign:'center' }}>
                <img src={form.imageUrl} alt={form.name}
                  style={{ maxWidth:'100%', maxHeight:200, objectFit:'contain', borderRadius:6, border:'1px solid var(--border)' }}
                  onError={e => e.target.style.display='none'}
                />
              </div>
            )}


            <div className="form-row">
              <div className="form-group">
                <label>SKU *</label>
                <input required value={form.sku} onChange={e => set('sku', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Vendor Item ID</label>
                <input value={form.vendorItemId} onChange={e => set('vendorItemId', e.target.value)} placeholder="Wintron itemid" />
              </div>
            </div>

            <div className="form-group">
              <label>Product name *</label>
              <input required value={form.name} onChange={e => set('name', e.target.value)} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Unit cost ($)</label>
                <input type="number" step="0.0001" min="0" value={form.unitCost} onChange={e => set('unitCost', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Price / MAP ($)</label>
                <input type="number" step="0.0001" min="0" value={form.unitPrice} onChange={e => set('unitPrice', e.target.value)} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Compare-at price ($)</label>
                <input type="number" step="0.0001" min="0" value={form.compareAtPrice} onChange={e => set('compareAtPrice', e.target.value)} placeholder="List price" />
              </div>
              <div className="form-group">
                <label>Weight (lbs)</label>
                <input type="number" step="0.0001" min="0" value={form.weight} onChange={e => set('weight', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Dimensions (in) L × W × H</label>
                <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                  <input type="number" step="0.01" min="0" value={form.lengthIn} onChange={e => set('lengthIn', e.target.value)} placeholder="L" style={{ flex:1 }} />
                  <span style={{ color:'var(--text-tertiary)', fontSize:12 }}>×</span>
                  <input type="number" step="0.01" min="0" value={form.widthIn} onChange={e => set('widthIn', e.target.value)} placeholder="W" style={{ flex:1 }} />
                  <span style={{ color:'var(--text-tertiary)', fontSize:12 }}>×</span>
                  <input type="number" step="0.01" min="0" value={form.heightIn} onChange={e => set('heightIn', e.target.value)} placeholder="H" style={{ flex:1 }} />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Warehouse qty</label>
                <input type="number" min="0" value={form.quantityOnHand} onChange={e => set('quantityOnHand', e.target.value)} />
              </div>
              <div className="form-group">

              </div>
            </div>

            <div className="form-group">

            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} />
            </div>

            <div style={{ display:'flex', gap:20, marginTop:4 }}>
              <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:13, fontWeight:'normal' }}>
                <input type="checkbox" style={{ width:'auto' }}
                  checked={form.includeInVendorFeed || false}
                  onChange={e => set('includeInVendorFeed', e.target.checked)} />
                Include in vendor feed
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:13, fontWeight:'normal' }}>
                <input type="checkbox" style={{ width:'auto' }}
                  checked={form.alertsEnabled !== false}
                  onChange={e => set('alertsEnabled', e.target.checked)} />
                Alerts enabled
              </label>
            </div>
            <div className="form-group" style={{ marginTop:12 }}>
              <label>Dimensions (in) L × W × H</label>
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <input type="number" step="0.01" min="0" value={form.lengthIn} onChange={e => set('lengthIn', e.target.value)} placeholder="L" style={{ flex:1 }} />
                <span style={{ color:'var(--text-tertiary)', fontSize:12 }}>×</span>
                <input type="number" step="0.01" min="0" value={form.widthIn} onChange={e => set('widthIn', e.target.value)} placeholder="W" style={{ flex:1 }} />
                <span style={{ color:'var(--text-tertiary)', fontSize:12 }}>×</span>
                <input type="number" step="0.01" min="0" value={form.heightIn} onChange={e => set('heightIn', e.target.value)} placeholder="H" style={{ flex:1 }} />
              </div>
            </div>
            <div style={{ marginTop:12 }}>
              <ImageGallery productId={product.id} />
            </div>
            {shopifyAccounts && shopifyAccounts.length > 0 && (
              <details style={{ borderTop:'0.5px solid var(--card-border)', paddingTop:12, marginTop:8 }}>
                <summary style={{ fontWeight:500, fontSize:13, cursor:'pointer', marginBottom:10, userSelect:'none' }}>
                  Shopify Store Assignments
                </summary>
                {shopifyAccounts.map(acct => {
                  const assignment = storeAssignments[acct.id] || { assigned:false, shopifyProductId:'', shopifyVariantId:'' };
                  return (
                    <div key={acct.id} style={{ background:'var(--page-bg)', borderRadius:8, padding:10, marginBottom:8 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:assignment.assigned?10:0 }}>
                        <input type="checkbox" id={`store-${acct.id}`} style={{ width:'auto' }}
                          checked={assignment.assigned}
                          onChange={e => setStoreAssignments(s => ({ ...s, [acct.id]:{ ...assignment, assigned:e.target.checked } }))} />
                        <label htmlFor={`store-${acct.id}`} style={{ marginBottom:0, fontWeight:500, cursor:'pointer' }}>
                          {acct.name} <span style={{ fontSize:11, color:'var(--text-tertiary)', fontWeight:400 }}>({acct.store_url})</span>
                        </label>
                      </div>
                      {assignment.assigned && (
                        <div className="form-row" style={{ marginLeft:24 }}>
                          <div className="form-group" style={{ margin:0 }}>
                            <label style={{ fontSize:11 }}>Shopify Variant ID</label>
                            <input value={assignment.shopifyVariantId}
                              onChange={e => setStoreAssignments(s => ({ ...s, [acct.id]:{ ...assignment, shopifyVariantId:e.target.value } }))}
                              placeholder="e.g. 123456789" />
                          </div>
                          <div className="form-group" style={{ margin:0 }}>
                            <label style={{ fontSize:11 }}>Shopify Product ID</label>
                            <input value={assignment.shopifyProductId}
                              onChange={e => setStoreAssignments(s => ({ ...s, [acct.id]:{ ...assignment, shopifyProductId:e.target.value } }))}
                              placeholder="e.g. 987654321" />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </details>
            )}
            {/* HazMat Section */}
            <details style={{ borderTop:'0.5px solid var(--card-border)', paddingTop:12, marginTop:8 }}>
              <summary style={{ fontWeight:500, fontSize:13, cursor:'pointer', marginBottom:10, userSelect:'none' }}>
                <i className="ti ti-alert-triangle" style={{ marginRight:6, color:'var(--amber)' }} />
                HazMat Shipping
              </summary>
              <div style={{ background:'var(--page-bg)', borderRadius:8, padding:12 }}>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', marginBottom:12 }}>
                  <input type="checkbox" style={{ width:'auto' }}
                    checked={form.hazmatEnabled || false}
                    onChange={e => set('hazmatEnabled', e.target.checked)} />
                  <span style={{ fontWeight:500, fontSize:13 }}>This product ships as HazMat</span>
                </label>
                {form.hazmatEnabled && (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                    <div className="form-group">
                      <label>UN Number</label>
                      <select value={form.hazmatUnNumber} onChange={e => set('hazmatUnNumber', e.target.value)}>
                        <option value="">Select…</option>
                        <option value="UN3480">UN3480 — Lithium ion batteries</option>
                        <option value="UN3481">UN3481 — Lithium ion batteries in equipment</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Packing Group</label>
                      <select value={form.hazmatPackingGroup} onChange={e => set('hazmatPackingGroup', e.target.value)}>
                        <option value="">Select…</option>
                        <option value="II">II</option>
                        <option value="III">III</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ gridColumn:'1/-1' }}>
                      <label>Proper Shipping Name</label>
                      <input value={form.hazmatProperName}
                        onChange={e => set('hazmatProperName', e.target.value)}
                        placeholder="e.g. LITHIUM ION BATTERY" />
                    </div>
                    <div className="form-group">
                      <label>Hazard Class</label>
                      <input value={form.hazmatClass} onChange={e => set('hazmatClass', e.target.value)}
                        placeholder="9" />
                    </div>
                    <div className="form-group">
                      <label>Ship Mode</label>
                      <select value={form.hazmatMode} onChange={e => set('hazmatMode', e.target.value)}>
                        <option value="ground">Ground only</option>
                        <option value="air">Air only</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Watt-hours per battery (Wh)</label>
                      <input type="number" step="0.1" min="0" value={form.hazmatWhPerUnit}
                        onChange={e => set('hazmatWhPerUnit', e.target.value)} placeholder="e.g. 256" />
                    </div>
                    <div className="form-group">
                      <label>Cells per battery</label>
                      <input type="number" min="1" value={form.hazmatCellsPerUnit}
                        onChange={e => set('hazmatCellsPerUnit', e.target.value)} placeholder="e.g. 4" />
                    </div>
                    <div className="form-group" style={{ gridColumn:'1/-1' }}>
                      <label>Packaging Type</label>
                      <input value={form.hazmatPackaging} onChange={e => set('hazmatPackaging', e.target.value)}
                        placeholder="FiberboardBox" />
                    </div>
                  </div>
                )}
              </div>
            </details>
          </div>


            {/* Signature Required Section */}
            <details style={{ borderTop:'0.5px solid var(--card-border)', paddingTop:12, marginTop:8 }}>
              <summary style={{ fontWeight:500, fontSize:13, cursor:'pointer', marginBottom:10, userSelect:'none' }}>
                <i className="ti ti-writing-sign" style={{ marginRight:6, color:'var(--accent)' }} />
                Delivery Options
              </summary>
              <div style={{ background:'var(--page-bg)', borderRadius:8, padding:12 }}>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                  <input type="checkbox" style={{ width:'auto' }}
                    checked={form.signatureRequired || false}
                    onChange={e => set('signatureRequired', e.target.checked)} />
                  <span style={{ fontWeight:500, fontSize:13 }}>Require signature on delivery</span>
                </label>
                <p style={{ fontSize:11, color:'var(--text-secondary)', margin:'6px 0 0 24px' }}>
                  When enabled, UPS will require an adult signature upon delivery for orders containing this product.
                </p>
              </div>
            </details>
          {/* RIGHT COLUMN — Description */}
          <div style={{ display:'flex', flexDirection:'column' }}>
            <div className="form-group" style={{ flex:1, display:'flex', flexDirection:'column' }}>
              <label>Description</label>
              <TinyEditor
                value={form.description}
                onChange={val => set('description', val)}
              />
            </div>

          </div>
        </div>
      </form>
    </Modal>
  );
}
